import React, { Suspense, useState, useRef, useCallback } from 'react';
import { Canvas, useLoader, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { Maximize2, Minimize2, Loader2 } from 'lucide-react';

function Scene({ image }) {
  const texture = useLoader(THREE.TextureLoader, image);
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  const meshRef = useRef();

  useFrame((_, delta) => {
    if (meshRef.current && meshRef.current.userData.autoRotate) {
      meshRef.current.rotation.y += delta * 0.05;
    }
  });

  return (
    <mesh ref={meshRef} userData={{ autoRotate: true }}>
      <sphereGeometry args={[500, 64, 64]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} />
    </mesh>
  );
}

function Controls({ setAutoRotate }) {
  const controlsRef = useRef();

  const handleStart = useCallback(() => {
    setAutoRotate(false);
    if (controlsRef.current) {
      controlsRef.current.autoRotate = false;
    }
  }, [setAutoRotate]);

  const handleEnd = useCallback(() => {
    setTimeout(() => setAutoRotate(true), 3000);
  }, [setAutoRotate]);

  return (
    <OrbitControls
      ref={controlsRef}
      enableZoom={true}
      zoomSpeed={0.6}
      minDistance={0.1}
      maxDistance={300}
      enablePan={false}
      rotateSpeed={0.8}
      minPolarAngle={0.1}
      maxPolarAngle={Math.PI - 0.1}
      onStart={handleStart}
      onEnd={handleEnd}
    />
  );
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full bg-gray-900">
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );
}

const PanoramaViewer360 = ({ scenes }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const containerRef = useRef();

  const handleSceneChange = (idx) => {
    setAutoRotate(true);
    setActiveIndex(idx);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  if (!scenes || scenes.length === 0) return null;

  const activeScene = scenes[activeIndex];

  return (
    <div className="mt-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">Recorrido 360°</h3>
      <p className="text-gray-600 mb-4 text-sm">
        Arrastra para explorar. Haz clic en las miniaturas para cambiar de escena.
      </p>

      <div
        ref={containerRef}
        className="relative rounded-xl overflow-hidden bg-gray-900"
        style={{ height: isFullscreen ? '100vh' : '60vh', minHeight: '400px' }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Canvas camera={{ position: [0, 0, 0.1], fov: 90 }}>
            <Scene image={activeScene.image} />
            <Controls setAutoRotate={setAutoRotate} />
          </Canvas>
        </Suspense>

        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <p className="text-white/80 text-center px-4 text-lg font-medium drop-shadow-lg">
            Tour 360° Interactivo — Usa el mouse para explorar
          </p>
        </div>

        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <button
            onClick={toggleFullscreen}
            className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>

        <div className="absolute bottom-4 left-0 right-0 z-10 px-4">
          <p className="text-white/80 text-sm font-medium text-center mb-2">
            {activeScene.location_name}
          </p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mt-4">
        {scenes.map((scene, idx) => (
          <button
            key={idx}
            onClick={() => handleSceneChange(idx)}
            className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden transition-all ${
              activeIndex === idx
                ? 'ring-2 ring-blue-500 shadow-md'
                : 'hover:ring-1 hover:shadow-sm opacity-70 hover:opacity-100'
            }`}
          >
            <img
              src={scene.thumbnail}
              alt={scene.location_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default PanoramaViewer360;
